const LEAFLET_JS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js';
const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';

/**
 * Подложка карты: список источников по убыванию качества, с автооткатом.
 *
 * Основа — **CARTO** (Voyager для светлой темы, Dark Matter для тёмной):
 * подробная цветная карта, как у крупных сервисов. С рабочим ключом
 * (`VITE_CARTO_API_KEY`) тайлы приходят чистыми; **без ключа CARTO вшивает
 * в каждый тайл надпись «API KEY REQUIRED»** — CSS-ом не снять.
 *
 * Ловушка мёртвого ключа: keyed-источник при неверном ключе отдаёт не
 * ошибку, а **валидную картинку** с надписью (CARTO — с HTTP 200, MapTiler
 * — с 403, но телом-PNG). Браузерный `<img>` её показывает, событие
 * `tileerror` не летит, автооткат по нему не срабатывает. Поэтому каждый
 * keyed-источник перед показом проверяется живым запросом (`probeTier`):
 * не-2xx или сетевой сбой → молча уходим на следующий уровень.
 *
 * Порядок (лучший первый):
 *  1. MapTiler Streets — если задан рабочий `VITE_MAPTILER_API_KEY`
 *     (`streets-v4` для светлой темы, `streets-v4-dark` для тёмной —
 *     тёмно-синяя, с читаемыми подписями, в отличие от чёрной CARTO Dark);
 *  2. CARTO Voyager/Dark — если задан `VITE_CARTO_API_KEY`;
 *  3. Esri Light/Dark Gray Canvas — совсем без ключа, блёклая, но всегда
 *     работает: запас на случай, когда ключей нет вовсе.
 */
const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined)?.trim() || undefined;
const CARTO_KEY = (import.meta.env.VITE_CARTO_API_KEY as string | undefined)?.trim() || undefined;

const OSM_ATTR = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const MAPTILER_ATTRIBUTION = `© <a href="https://www.maptiler.com/copyright/">MapTiler</a> ${OSM_ATTR}`;
const CARTO_ATTRIBUTION = `${OSM_ATTR} © <a href="https://carto.com/attributions">CARTO</a>`;
const ESRI_ATTRIBUTION = `Tiles © <a href="https://www.esri.com/">Esri</a> — ${OSM_ATTR}`;

interface TileTier {
  url: string;
  attribution: string;
  /** Требует проверки ключа живым запросом перед показом. */
  keyed?: boolean;
  /** До какого зума у источника есть свои тайлы; выше Leaflet растянет. */
  maxNativeZoom?: number;
}

/** Источники подложки для темы, лучший первый. */
function tileTiers(theme: 'light' | 'dark'): TileTier[] {
  const out: TileTier[] = [];

  if (MAPTILER_KEY) {
    const style = theme === 'dark' ? 'streets-v4-dark' : 'streets-v4';
    out.push({
      url: `https://api.maptiler.com/maps/${style}/256/{z}/{x}/{y}{r}.png?key=${MAPTILER_KEY}`,
      attribution: MAPTILER_ATTRIBUTION,
      keyed: true,
    });
  }

  if (CARTO_KEY) {
    const style = theme === 'dark' ? 'dark_all' : 'voyager';
    out.push({
      url: `https://{s}.basemaps.cartocdn.com/rastertiles/${style}/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`,
      attribution: CARTO_ATTRIBUTION,
      keyed: true,
    });
  }

  out.push({
    // Порядок сегментов у Esri — {z}/{y}/{x}, без {s} и {r}.
    url: `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_${
      theme === 'dark' ? 'Dark' : 'Light'
    }_Gray_Base/MapServer/tile/{z}/{y}/{x}`,
    attribution: ESRI_ATTRIBUTION,
    maxNativeZoom: 16,
  });

  return out;
}

/** Оставлено для обратной совместимости — новый код использует `addThemedTileLayer`. */
export const TILE_URL = tileTiers('light')[0]!.url;
export const TILE_ATTRIBUTION = tileTiers('light')[0]!.attribution;

declare global {
  interface Window {
    L?: any;
  }
}

let loading: Promise<void> | null = null;

/**
 * Leaflet подгружается по требованию и один раз на всё приложение.
 *
 * Класть карту в основной бандл незачем: её открывает меньшинство,
 * а весит она больше всего остального интерфейса. Обещание запоминается,
 * иначе две карты на экране начнут грузить библиотеку каждая себе.
 */
export function loadLeaflet(): Promise<void> {
  if (window.L) return Promise.resolve();
  if (loading) return loading;

  loading = new Promise<void>((resolve, reject) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = LEAFLET_CSS;
    document.head.appendChild(css);

    const js = document.createElement('script');
    js.src = LEAFLET_JS;
    js.onload = () => resolve();
    js.onerror = () => {
      // Сбрасываем, иначе одна неудача навсегда закрывает карту:
      // повторная попытка вернула бы то же отклонённое обещание.
      loading = null;
      reject(new Error('Карта не загрузилась'));
    };
    document.head.appendChild(js);
  });

  return loading;
}

function currentTheme(): 'light' | 'dark' {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * Подложка, которая сама переключается вслед за темой приложения и сама
 * перебирает источники (`tileTiers`), если текущий не отвечает.
 *
 * Пересоздавать слой при каждой смене темы незачем — у Leaflet-тайлов есть
 * `setUrl`, а слежение снимается тем же способом, что и сама карта:
 * событие `unload` летит из `map.remove()`, который и так уже вызывается
 * при размонтировании компонента.
 */
export function addThemedTileLayer(map: any): void {
  const L = window.L;
  let theme = currentTheme();
  let tiers = tileTiers(theme);
  let idx = 0;
  let attribution = tiers[0]!.attribution;

  const layer = L.tileLayer(tiers[0]!.url, {
    maxZoom: 19,
    maxNativeZoom: tiers[0]!.maxNativeZoom ?? 19,
    attribution,
  }).addTo(map);

  function applyTier(): void {
    const t = tiers[idx]!;
    map.attributionControl?.removeAttribution(attribution);
    attribution = t.attribution;
    layer.options.maxNativeZoom = t.maxNativeZoom ?? 19;
    layer.setUrl(t.url);
    map.attributionControl?.addAttribution(attribution);
    if (t.keyed) probeTier(idx);
  }

  /*
   * Проверка ключа живым запросом.
   *
   * Мёртвый ключ MapTiler отдаёт водяной PNG с кодом 403 — `<img>` его
   * покажет, `tileerror` не сработает. `fetch` статус видит: не-2xx или
   * сетевой сбой — уходим на следующий уровень (Esri без ключа). Гонки
   * нет: `idx === i` гарантирует, что мы всё ещё на том источнике,
   * который проверяли (тему могли переключить, пока летел запрос).
   */
  function probeTier(i: number): void {
    const probe = tiers[i]!.url
      .replace('{z}', '1').replace('{x}', '1').replace('{y}', '1')
      .replace('{r}', '').replace('{s}', 'a');
    fetch(probe, { method: 'GET', mode: 'cors' })
      .then((r) => {
        if (!r.ok && idx === i && idx < tiers.length - 1) {
          idx += 1;
          applyTier();
        }
      })
      .catch(() => {
        if (idx === i && idx < tiers.length - 1) {
          idx += 1;
          applyTier();
        }
      });
  }

  // Одна карта грузит десятки тайлов — при сломанном источнике ошибка
  // прилетит на каждый. Переключаемся на следующий уровень один раз за
  // источник, а не на каждый повторный сбой.
  layer.on('tileerror', () => {
    if (idx >= tiers.length - 1) return;
    idx += 1;
    applyTier();
  });

  // Проверяем ключ и у самого первого источника, если он keyed.
  if (tiers[0]!.keyed) probeTier(0);

  const observer = new MutationObserver(() => {
    const next = currentTheme();
    if (next === theme) return;
    theme = next;
    tiers = tileTiers(theme);
    idx = 0;
    applyTier();
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  map.once('unload', () => observer.disconnect());
}

/**
 * Геопозиция пользователя для кнопки «где я» на карте.
 *
 * Живёт в `telegram.ts` (`requestLocation`): в новых клиентах Telegram
 * браузерная геолокация в Mini App не работает, нужен `LocationManager`.
 * Здесь — тонкий ре-экспорт, чтобы компоненты карт не знали про Telegram.
 */
export { requestLocation as locateUser } from './telegram';
